// Canvas 1: Maturity Snapshot. Reference definition (with Canvas 0): tables with fixed rows, calc cells, a pull block, results, a reference panel.
import { makeDef, G, text, long, num, pick, tag, calc, fields, table, pull, note, reference } from './base.js';
import { has, S, N, T, roundHalfUp, fix, gap } from '../helpers.js';

const DIMS = [
  { id: 'cx', name: 'CX', sub: 'journey ownership and experience strategy' },
  { id: 'data', name: 'Data and AI', sub: 'quality, access, model readiness' },
  { id: 'people', name: 'People and Governance', sub: 'skills, decision rights, controls' },
];
const LEVELS = ['Ad hoc', 'Emerging', 'Defined', 'Managed', 'Optimised'];
const LEVEL_OPTS = LEVELS.map((name, i) => ({ v: String(i + 1), label: String(i + 1), hint: name }));
const DEFAULT_WEIGHTS = { cx: 30, data: 40, people: 30 };
const fixedRows = DIMS.map((d) => ({ id: d.id, label: d.name, sub: d.sub }));

const RUBRIC_HEAD = ['Level', 'CX: journey ownership and experience strategy', 'Data and AI: quality, access, model readiness', 'People and Governance: skills, decision rights, controls'];
const RUBRIC = [
  ['1 Ad hoc', 'No journey is mapped end to end; each channel team holds its own view of the customer.', 'Five or more systems hold customer data with no shared customer ID; no model in production.', 'No AI decision forum; Risk and Compliance see projects only after launch.'],
  ['2 Emerging', 'Some journeys mapped, per channel only; no single owner of any full journey.', 'Data still in five or more systems, but a model exists (pilot or live) with no CX use case attached.', 'Reviews happen after launch or after an incident; no single named owner across CX, data and risk.'],
  ['3 Defined', 'Priority journeys documented end to end, reviewed at least yearly, each with a named owner.', 'Customer data for priority journeys reachable from one place; at least one model in production and monitored.', 'An AI decision forum exists with CX, data and risk at the table, but meets only when called.'],
  ['4 Managed', 'Journey performance tracked on one shared dashboard; the owner adjusts the experience monthly.', 'Shared customer ID across systems; each production model tied to a named CX use case and owner.', 'The forum meets on a fixed rhythm, reviews AI decisions and risk, and has a written escalation path.'],
  ['5 Optimised', 'All priority journeys owned and run from live data; the owner is accountable to an executive KPI.', 'Most models live, monitored and tied to an executive KPI.', 'Every case reviewed before launch and monitored after; roles and skills redesigned for AI-enabled work.'],
];
const HAND_RULES = [
  ['Target', 'Choose the level whose proof you plan to be able to show in 12 months. If you cannot name the proof, lower the target.'],
  ['Gap', 'Gap = target minus level now.'],
  ['Weight', 'The weights total 100 per cent. The default is 30, 40, 30. If you move one, write why in "Why this weight".'],
  ['Weighted gap', 'Weighted gap = (target minus level now) x weight. A gap of 2 at a weight of 40 per cent is 2 x 0.40 = 0.80.'],
  ['Priority gap', 'The largest weighted gap is the suggested priority gap. Confirm it, or override it and write the reason. If two tie, take the dimension with the lower level now.'],
  ['Reading the result', 'Weighted level = level now x weight for each row, added up. Read it as the nearest whole level: 2.6 reads as Level 3, Defined.'],
];

const reads = (x) => {
  const n = Math.max(1, Math.min(5, roundHalfUp(x, 0)));
  return `Level ${n}, ${LEVELS[n - 1]}`;
};

const baselineLine = (b) => {
  const val = b.value ? `${b.value}${b.unit ? ` ${b.unit}` : ''}` : 'not yet known';
  return `${b.measure}: ${val}${b.tag ? ` (${b.tag})` : ''}`;
};

const NORTHSTAR = {
  ...T(
    'lvl',
    [
      { level: '2', proof: 'Journeys mapped per channel only; no owner of a full journey', tag: 'M' },
      { level: '2', proof: 'Customer data in five systems; a churn model exists with no CX use', tag: 'M' },
      { level: '1', proof: 'No AI decision forum; Risk sees projects only after launch', tag: 'E' },
    ],
    ['cx', 'data', 'people'],
  ),
  ...T('tgt', [{ target: '3' }, { target: '3' }, { target: '3' }], ['cx', 'data', 'people']),
  ...T(
    'wt',
    [
      { weight: '30', why: 'Customers feel the journey first' },
      { weight: '40', why: 'Every candidate use case depends on data in five systems' },
      { weight: '30', why: 'Where decisions and controls sit' },
    ],
    ['cx', 'data', 'people'],
  ),
  'prio.confirm': 'People and Governance',
  'so.reviewer': 'Business head',
  'so.objection': 'Show me what changes for a customer if this moves from a 1 to a 3.',
  'so.evidence': 'Today a generic decline; at level 3 a reason and a call: Canvas 2, stage 3',
  'so.owner': 'Head of Retail Banking',
  'so.status': 'Answered',
};

export default makeDef(1, {
  short: 'Maturity',
  purpose: 'Score where your organisation really stands on three dimensions, set a 12-month target, and find the one gap that matters most.',
  howTo: [
    'Score each dimension against the five-level rubric, then write the proof you could show. A level without proof does not count: it is the level below.',
    'Set a target level for 12 months, then weight the dimensions. The canvas works out each gap and the priority gap for you.',
    'Everything calculated here travels on to Canvases 3, 5, 6, 7 and 9.',
  ],
  results: [
    { id: 'now', label: 'Weighted level now', rule: 'Level now x weight, row by row, then add.' },
    { id: 'nowReads', label: 'Reads as', rule: 'The nearest whole level, and its name.' },
    { id: 'target', label: 'Weighted target', rule: 'Target x weight, row by row, then add.' },
    { id: 'targetReads', label: 'Target reads as', rule: 'The nearest whole level, and its name.' },
    { id: 'confidence', label: 'Evidence confidence', rule: 'Count the levels tagged M or E.' },
  ],
  steps: [
    {
      title: 'Score alone, with proof and a tag',
      hint: 'Score what you can show, against the rubric. Write the proof and tag it M, E or G. A level without proof does not count: it is the level below.',
      blocks: [
        pull('p1', 'From your Case Card', [
          { id: 'journey', label: 'Journey', ref: 'Case Card B', no: 0, get: (up) => up[0]?.d.facts?.journey || null },
          {
            id: 'baselines',
            label: 'Baselines',
            ref: 'Case Card D',
            no: 0,
            get: (up) => {
              const list = (up[0]?.d.facts?.baselines || []).filter((b) => b.measure);
              return list.length ? list.map(baselineLine).join('; ') : null;
            },
            note: 'Your baseline numbers and their tags are proof you can show.',
          },
        ]),
        table('lvl', {
          rows: { fixed: fixedRows },
          pdf: 'table',
          columns: [
            pick('level', 'Level now', LEVEL_OPTS, {
              required: true,
              w: 2,
              guide: G(
                'Pick the level whose proof you can show. If you cannot show it, it is the level below.',
                'CX is level 2: journeys are mapped per channel only, with no owner of a full journey.',
                '1 Ad hoc, 2 Emerging, 3 Defined, 4 Managed, 5 Optimised.',
                'Scoring the level you hope to reach. Score where you stand today.',
              ),
            }),
            long('proof', 'Proof I can show', {
              required: true,
              w: 3,
              rows: 2,
              placeholder: 'A fact someone could check',
              guide: G(
                'Write the fact that backs your level: a report, a system, a decision.',
                'Customer data in five systems; a churn model exists with no CX use.',
                undefined,
                'Opinions are not proof. "We are good at data" is a view; "five systems, no shared ID" is a fact.',
              ),
            }),
            tag('tag', 'Tag', {
              required: true,
              w: 1,
              guide: G('M if the proof comes from a report or system; E if it is informed and has a stated basis; G if there is no basis yet.', 'M', 'A level tagged E or G goes to the Evidence Gaps list.'),
            }),
          ],
        }),
        reference('The five levels: what each looks like', RUBRIC_HEAD, RUBRIC),
      ],
    },
    {
      title: 'Compare at the table and set targets',
      hint: 'At your table, compare the proof behind each level, not the numbers. Then set the level whose proof you plan to show in 12 months.',
      blocks: [
        table('tgt', {
          rows: { fixed: fixedRows },
          pdf: 'table',
          columns: [
            pick('target', 'Target in 12 months', LEVEL_OPTS, {
              required: true,
              w: 2,
              guide: G(
                'Pick the level whose proof you plan to be able to show in 12 months.',
                'A target of 3 on every dimension.',
                undefined,
                'If you cannot name the proof for the target level, lower the target.',
              ),
            }),
            calc('gap', 'Gap', { w: 1, rule: 'target minus level now' }),
          ],
        }),
      ],
    },
    {
      title: 'Set the weights and find the priority gap',
      hint: 'The weights total 100 per cent. The default is 30, 40, 30: if you move one, write why. The canvas then works out each weighted gap.',
      blocks: [
        table('wt', {
          rows: { fixed: fixedRows },
          pdf: 'table',
          columns: [
            num('weight', 'Weight', {
              required: true,
              w: 1,
              unit: '%',
              min: 0,
              max: 100,
              default: (row) => String(DEFAULT_WEIGHTS[row.id]),
              guide: G(
                'How much this dimension matters on your journey. The three weights total 100 per cent. The default is 30, 40, 30.',
                '30, 40 and 30.',
                'The weights must total 100.',
                'If you move a weight, write why in the next column.',
              ),
            }),
            text('why', 'Why this weight', {
              w: 2,
              guide: G('One line on why. Required in spirit when you move a weight away from the default.', 'Every candidate use case depends on data in five systems'),
            }),
            calc('wgap', 'Weighted gap', { w: 1, rule: 'gap x weight' }),
          ],
        }),
        fields([
          calc('wt_total', 'Weights total', { w: 1, rule: 'must total 100' }),
          calc('wgap_total', 'Weighted gaps total', { w: 1, rule: 'add the three' }),
          calc('prio.suggested', 'Suggested priority gap', { w: 2, rule: 'the largest weighted gap; a tie goes to the lower level now' }),
          pick('prio.confirm', 'Confirm or override', DIMS.map((d) => d.name), {
            required: true,
            w: 2,
            guide: G(
              'Choose the dimension you will treat as the priority gap. It can be the suggestion, or you can override it.',
              'People and Governance',
              undefined,
              'If you override, write the reason in the next box.',
            ),
          }),
          text('prio.reason', 'Reason, if you override the suggestion', {
            w: 2,
            required: ({ v, d }) => has(v['prio.confirm']) && has(d.calc['prio.suggested']) && v['prio.confirm'] !== d.calc['prio.suggested'],
            guide: G('Say why you chose a different dimension from the suggestion.'),
          }),
        ]),
        reference('Work it out by hand', ['Step', 'Rule'], HAND_RULES),
      ],
    },
  ],
  derive(v) {
    const rowsOut = DIMS.map((dm) => {
      const level = N(v, `lvl.${dm.id}.level`);
      const target = N(v, `tgt.${dm.id}.target`);
      const weight = N(v, `wt.${dm.id}.weight`);
      const g = level !== null && target !== null ? target - level : null;
      const wg = g !== null && weight !== null ? (g * weight) / 100 : null;
      return {
        id: dm.id,
        name: dm.name,
        level,
        levelName: level ? LEVELS[level - 1] : '',
        tag: S(v, `lvl.${dm.id}.tag`),
        proof: S(v, `lvl.${dm.id}.proof`),
        target,
        weight,
        why: S(v, `wt.${dm.id}.why`),
        gap: g,
        wgap: wg,
      };
    });
    const out = { calc: {}, results: {}, evidence: [], warnings: [], blockers: [] };
    rowsOut.forEach((r) => {
      out.calc[`tgt.${r.id}.gap`] = r.gap === null ? '' : String(r.gap);
      out.calc[`wt.${r.id}.wgap`] = r.wgap === null ? '' : fix(r.wgap, 2);
    });
    const weights = rowsOut.map((r) => r.weight);
    const allWeights = weights.every((w) => w !== null);
    const wtTotal = allWeights ? weights.reduce((a, b) => a + b, 0) : null;
    out.calc.wt_total = wtTotal === null ? '' : String(wtTotal);
    const wgaps = rowsOut.map((r) => r.wgap);
    out.calc.wgap_total = wgaps.every((x) => x !== null) ? fix(wgaps.reduce((a, b) => a + b, 0), 2) : '';
    // suggested priority gap: the largest weighted gap; a tie goes to the lower level now
    let suggested = '';
    if (wgaps.every((x) => x !== null)) {
      const order = rowsOut
        .map((r, i) => ({ r, i }))
        .sort((a, b) => Math.round(b.r.wgap * 1e4) - Math.round(a.r.wgap * 1e4) || a.r.level - b.r.level || a.i - b.i);
      suggested = order[0].r.name;
    }
    out.calc['prio.suggested'] = suggested;
    const levelsOk = rowsOut.every((r) => r.level !== null);
    const targetsOk = rowsOut.every((r) => r.target !== null);
    const now = levelsOk && allWeights ? rowsOut.reduce((a, r) => a + r.level * r.weight, 0) / 100 : null;
    const target = targetsOk && allWeights ? rowsOut.reduce((a, r) => a + r.target * r.weight, 0) / 100 : null;
    const tagged = rowsOut.filter((r) => r.tag === 'M' || r.tag === 'E').length;
    out.results = {
      now: now === null ? '' : fix(now, 1),
      nowReads: now === null ? '' : reads(now),
      target: target === null ? '' : fix(target, 1),
      targetReads: target === null ? '' : reads(target),
      confidence: rowsOut.some((r) => r.tag) ? `${tagged} of 3` : '',
    };
    rowsOut.forEach((r) => {
      if (r.level !== null && (r.tag === 'E' || r.tag === 'G')) {
        out.evidence.push(gap(`Maturity level: ${r.name} (level ${r.level})`, r.tag, `Canvas 1, ${r.name}`));
      }
      if (r.level !== null && !r.proof) out.warnings.push({ step: 's1', text: `${r.name}: a level without proof does not count. It is the level below.` });
      if (r.level !== null && r.target !== null && r.target < r.level) out.warnings.push({ step: 's2', text: `${r.name}: the target is below the level now.` });
    });
    if (wtTotal !== null && wtTotal !== 100) out.blockers.push({ step: 's3', text: `The weights total ${wtTotal}. They must total 100.` });
    const confirmed = S(v, 'prio.confirm');
    out.facts = {
      dims: Object.fromEntries(rowsOut.map((r) => [r.id, r])),
      weightedNow: now === null ? null : roundHalfUp(now, 1),
      nowReads: out.results.nowReads,
      weightedTarget: target === null ? null : roundHalfUp(target, 1),
      targetReads: out.results.targetReads,
      suggested,
      priorityGap: confirmed || suggested,
    };
    return out;
  },
  NORTHSTAR,
});
